import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight, MapPin } from 'lucide-react'
import type { Guest, PublicWedding } from '@shared/types'
import { ApiError, api, mensajeDeError } from '../../lib/api'
import { fechaCompleta, fechaLarga, plural } from '../../lib/format'
import { descargarIcs, icsDeBoda } from '../../lib/ics'
import { Button, ErrorMessage, Field, Spinner, TextArea } from '../../ui'
import { CordMark, CordSpine } from '../../ui/Cord'
import {
  BOTON_CLARO,
  BOTON_CONTORNO,
  EnlaceExterno,
  claveDeConfirmacion,
  cx,
  tieneTexto,
  urlSegura,
} from './secciones/comunes'

/* ===========================================================================
   Confirmar asistencia — el trámite del invitado.

   Cero fricción: sin cuenta, sin app y con una sola mano. Tres pasos en la
   misma página y uno a la vez, para que nunca haya más de una pregunta en
   pantalla: buscarse, responder, listo.

   El cordón sigue en el borde izquierdo, igual que en la invitación, pero aquí
   no se ata al scroll: se traza con el paso en el que va el invitado. Los tres
   nudos son los tres pasos.
   =========================================================================== */

/** El mismo riel que la página pública, para que el cordón no se corte al entrar. */
const RIEL = 'w-[18px] sm:w-7 lg:w-9'
const RIEL_HUECO = 'pl-[18px] sm:pl-7 lg:pl-9'

/** Tope del campo de acompañantes. El servidor recorta al cupo real. */
const MAX_ACOMPANANTES = 10

/** Lo mismo que exige el servidor: con menos letras no se busca. */
const MINIMO_BUSQUEDA = 3

type Paso = 'buscar' | 'responder' | 'hecho'

/** Lo único que devuelve el buscador: nunca el código ni el correo de nadie. */
interface Coincidencia {
  id: string
  fullName: string
}

interface CuerpoRsvp {
  guestId: string
  attending: boolean
  code?: string
  companions?: number
  dietaryNotes?: string
  message?: string
}

interface Respuesta {
  guest: Guest
  /** Cuántos acompañantes pidió, para poder explicar si el cupo era menor. */
  pedidos: number
  dejoMensaje: boolean
}

const AVANCE: Record<Paso, number> = { buscar: 0.34, responder: 0.67, hecho: 1 }

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

/** Los códigos se dictan por teléfono: se comparan en mayúsculas y sin espacios. */
function normalizarCodigo(bruto: string): string {
  return bruto.trim().toUpperCase()
}

function acompanantesValidos(bruto: string): number {
  const n = Number.parseInt(bruto, 10)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(n, MAX_ACOMPANANTES)
}

function primerNombre(nombre: string): string {
  return nombre.trim().split(/\s+/)[0] ?? nombre
}

/**
 * Deja un rastro en este navegador para que la invitación no siga insistiendo
 * con el botón flotante. Si el almacenamiento está bloqueado no pasa nada: el
 * RSVP ya quedó guardado en el servidor, que es lo que cuenta.
 */
function recordarConfirmacion(slug: string, guest: Guest): void {
  try {
    localStorage.setItem(
      claveDeConfirmacion(slug),
      JSON.stringify({ guestId: guest.id, status: guest.status, en: new Date().toISOString() }),
    )
  } catch {
    // Navegación privada con almacenamiento bloqueado.
  }
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function Confirmar() {
  const { slug = '' } = useParams()

  // --- La boda -------------------------------------------------------------
  const [boda, setBoda] = useState<PublicWedding | null>(null)
  const [cargando, setCargando] = useState(true)
  const [fallo, setFallo] = useState<{ noEncontrada: boolean; mensaje: string } | null>(null)
  const [intento, setIntento] = useState(0)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    setFallo(null)

    api
      .get<{ wedding: PublicWedding }>(`/api/public/${encodeURIComponent(slug)}`)
      .then(({ wedding }) => {
        if (vigente) setBoda(wedding)
      })
      .catch((error: unknown) => {
        if (!vigente) return
        setBoda(null)
        setFallo({
          noEncontrada: error instanceof ApiError && error.esNoEncontrado,
          mensaje: mensajeDeError(error),
        })
      })
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [slug, intento])

  useEffect(() => {
    if (!boda) return
    const anterior = document.title
    document.title = `Confirmar · ${boda.partner1Name} y ${boda.partner2Name}`
    return () => {
      document.title = anterior
    }
  }, [boda])

  // --- Paso 1: buscarse ----------------------------------------------------
  const [consulta, setConsulta] = useState('')
  const [coincidencias, setCoincidencias] = useState<Coincidencia[] | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null)
  /** Contador de peticiones: descarta las respuestas que llegan tarde. */
  const peticion = useRef(0)

  const buscar = useCallback(
    async (termino: string) => {
      const mia = ++peticion.current
      try {
        const { matches } = await api.post<{ matches: Coincidencia[] }>(
          `/api/public/${encodeURIComponent(slug)}/rsvp/buscar`,
          { query: termino },
        )
        if (peticion.current !== mia) return
        setCoincidencias(matches)
        setErrorBusqueda(null)
      } catch (error: unknown) {
        if (peticion.current !== mia) return
        setCoincidencias(null)
        setErrorBusqueda(mensajeDeError(error))
      } finally {
        if (peticion.current === mia) setBuscando(false)
      }
    },
    [slug],
  )

  // --- Paso 2: responder ---------------------------------------------------
  const [elegido, setElegido] = useState<Coincidencia | null>(null)
  const [asiste, setAsiste] = useState<boolean | null>(null)
  const [codigo, setCodigo] = useState('')
  const [acompanantes, setAcompanantes] = useState('')
  const [dieta, setDieta] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null)

  // --- Paso 3: hecho -------------------------------------------------------
  const [respuesta, setRespuesta] = useState<Respuesta | null>(null)

  const paso: Paso = respuesta !== null ? 'hecho' : elegido !== null ? 'responder' : 'buscar'

  // Deep link del correo ({{linkConfirmar}}): aterriza directo en el paso de
  // responder, con el código ya escrito. El nombre no viaja en la URL a
  // propósito —nada personal en el query— así que el título dice «Tu
  // invitación» hasta que el servidor responda con el nombre real.
  const [searchParams] = useSearchParams()
  const deepLinkAplicado = useRef(false)
  useEffect(() => {
    if (deepLinkAplicado.current) return
    deepLinkAplicado.current = true

    const invitadoParam = (searchParams.get('invitado') ?? '').trim()
    if (invitadoParam === '') return
    setElegido({ id: invitadoParam, fullName: '' })

    const codigoParam = (searchParams.get('codigo') ?? '').trim()
    if (codigoParam !== '') setCodigo(codigoParam.toUpperCase())
  }, [searchParams])

  // Buscar mientras escribe: desde 3 letras y con 350 ms de calma.
  useEffect(() => {
    if (elegido !== null) return

    const termino = consulta.trim()
    if (termino.length < MINIMO_BUSQUEDA) {
      peticion.current++
      setCoincidencias(null)
      setErrorBusqueda(null)
      setBuscando(false)
      return
    }

    setBuscando(true)
    const temporizador = window.setTimeout(() => void buscar(termino), 350)
    return () => window.clearTimeout(temporizador)
  }, [consulta, elegido, buscar])

  // El título de cada paso recibe el foco al cambiar: quien navega con teclado
  // o lector de pantalla no se queda atrás en la página.
  const tituloRef = useRef<HTMLHeadingElement>(null)
  const primeraVez = useRef(true)

  useEffect(() => {
    if (primeraVez.current) {
      primeraVez.current = false
      return
    }
    tituloRef.current?.focus()
  }, [paso])

  function elegir(coincidencia: Coincidencia) {
    setElegido(coincidencia)
    setAsiste(null)
    setCodigo('')
    setAcompanantes('')
    setDieta('')
    setMensaje('')
    setErrorEnvio(null)
  }

  function volverABuscar() {
    setRespuesta(null)
    setElegido(null)
    setErrorEnvio(null)
  }

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (!boda || !elegido || asiste === null) return

    const rsvp = boda.settings.rsvp
    const codigoLimpio = normalizarCodigo(codigo)
    if (rsvp.requireCode && codigoLimpio === '') {
      setErrorEnvio('Escribe el código que aparece en tu invitación.')
      return
    }

    const pedidos = asiste ? acompanantesValidos(acompanantes) : 0
    const dietaLimpia = dieta.trim()
    const mensajeLimpio = mensaje.trim()
    const conMensaje = rsvp.allowMessages && mensajeLimpio !== ''

    const cuerpo: CuerpoRsvp = { guestId: elegido.id, attending: asiste }
    if (rsvp.requireCode) cuerpo.code = codigoLimpio
    if (asiste) cuerpo.companions = pedidos
    // Solo se manda si escribió algo: un campo vacío borraría la nota que los
    // novios pudieron dejar en la ficha del invitado.
    if (dietaLimpia !== '') cuerpo.dietaryNotes = dietaLimpia
    if (conMensaje) cuerpo.message = mensajeLimpio

    setEnviando(true)
    setErrorEnvio(null)

    try {
      const { guest } = await api.post<{ guest: Guest }>(
        `/api/public/${encodeURIComponent(slug)}/rsvp`,
        cuerpo,
      )
      setRespuesta({ guest, pedidos, dejoMensaje: conMensaje })
      recordarConfirmacion(slug, guest)
    } catch (error: unknown) {
      setErrorEnvio(mensajeDeError(error))
    } finally {
      setEnviando(false)
    }
  }

  if (cargando) return <Cargando />
  if (fallo?.noEncontrada) return <SinBoda />
  if (fallo) return <FalloDeCarga mensaje={fallo.mensaje} onReintentar={() => setIntento((n) => n + 1)} />
  if (!boda) return <SinBoda />

  return (
    <div className="relative min-h-dvh bg-bruma">
      <div
        aria-hidden="true"
        className={cx('fixed inset-y-0 left-0 z-20 bg-tinta text-laton-claro', RIEL)}
      >
        <CordSpine progress={AVANCE[paso]} knots={[0.34, 0.67, 1]} className="h-full w-full" />
      </div>

      <main className={cx('min-h-dvh', RIEL_HUECO)}>
        <Encabezado boda={boda} />

        <div className="px-5 py-8 sm:px-10 sm:py-10 lg:px-16">
          {/* Centrada como el resto del sitio: en pantalla ancha el aire se
              reparte a ambos lados en vez de morir todo a la derecha. */}
          <div className="mx-auto w-full max-w-xl">
            {paso === 'buscar' && (
              <PasoBuscar
                tituloRef={tituloRef}
                boda={boda}
                consulta={consulta}
                onConsulta={setConsulta}
                coincidencias={coincidencias}
                buscando={buscando}
                error={errorBusqueda}
                onBuscarYa={() => {
                  const termino = consulta.trim()
                  if (termino.length >= MINIMO_BUSQUEDA) void buscar(termino)
                }}
                onElegir={elegir}
              />
            )}

            {paso === 'responder' && elegido && (
              <PasoResponder
                tituloRef={tituloRef}
                boda={boda}
                invitado={elegido}
                asiste={asiste}
                onAsiste={setAsiste}
                codigo={codigo}
                onCodigo={setCodigo}
                acompanantes={acompanantes}
                onAcompanantes={setAcompanantes}
                dieta={dieta}
                onDieta={setDieta}
                mensaje={mensaje}
                onMensaje={setMensaje}
                enviando={enviando}
                error={errorEnvio}
                onEnviar={enviar}
                onOtroNombre={volverABuscar}
              />
            )}

            {paso === 'hecho' && respuesta && (
              <PasoHecho
                tituloRef={tituloRef}
                boda={boda}
                respuesta={respuesta}
                onCambiar={() => setRespuesta(null)}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Encabezado
// ---------------------------------------------------------------------------

function Encabezado({ boda }: { boda: PublicWedding }) {
  const fecha = fechaCompleta(boda.weddingDate)

  return (
    <header className="bg-tinta px-5 pb-8 pt-6 sm:px-10 sm:pb-10 sm:pt-8 lg:px-16">
      {/* Mismo eje que la columna de pasos de abajo. */}
      <div className="mx-auto w-full max-w-xl">
        <Link
          to={`/b/${boda.slug}`}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm text-palo transition-colors hover:text-bruma"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Volver a la invitación
        </Link>

        <p className="eyebrow mt-6 text-laton-claro">Confirmación</p>
        <h1 className="mt-3 font-display text-titulo text-bruma">
          {boda.partner1Name} y {boda.partner2Name}
        </h1>
        {fecha !== '' && <p className="mt-3 text-sm text-palo">{fecha}</p>}
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Paso 1 — buscarse
// ---------------------------------------------------------------------------

interface PasoBuscarProps {
  tituloRef: RefObject<HTMLHeadingElement | null>
  boda: PublicWedding
  consulta: string
  onConsulta: (valor: string) => void
  coincidencias: Coincidencia[] | null
  buscando: boolean
  error: string | null
  onBuscarYa: () => void
  onElegir: (coincidencia: Coincidencia) => void
}

function PasoBuscar({
  tituloRef,
  boda,
  consulta,
  onConsulta,
  coincidencias,
  buscando,
  error,
  onBuscarYa,
  onElegir,
}: PasoBuscarProps) {
  const limite = fechaLarga(boda.settings.rsvp.deadline) || boda.settings.rsvp.deadline.trim()
  const corta = consulta.trim().length > 0 && consulta.trim().length < MINIMO_BUSQUEDA

  return (
    <>
      <p className="eyebrow">Tu nombre</p>
      <h2 ref={tituloRef} tabIndex={-1} className="mt-3 font-display text-titulo text-tinta">
        Búscate en la lista
      </h2>
      <p className="mt-4 leading-relaxed text-tinta/85">
        Escribe tu nombre como te conocen los novios y tócate en la lista. No hace falta crear
        ninguna cuenta.
      </p>
      {tieneTexto(limite) && (
        <p className="mt-2 text-sm text-palo-hondo">Nos ayuda mucho si respondes antes del {limite}.</p>
      )}

      <form
        className="mt-8"
        onSubmit={(evento) => {
          evento.preventDefault()
          onBuscarYa()
        }}
      >
        <Field
          label="¿Cómo te llamas?"
          value={consulta}
          onChange={(evento) => onConsulta(evento.target.value)}
          hint={
            corta
              ? 'Escribe al menos 3 letras.'
              : 'Con el nombre o el apellido basta. Buscamos desde las 3 letras.'
          }
          autoComplete="name"
          autoCapitalize="words"
          enterKeyHint="search"
          autoFocus
          className="min-h-[52px] text-base"
        />
      </form>

      {/* Un solo punto de anuncio para lo que cambia solo mientras escribe. */}
      <div className="mt-6" role="status">
        {buscando && (
          <p className="flex items-center gap-3 text-sm text-palo-hondo">
            <Spinner small />
            Buscando…
          </p>
        )}
        {!buscando && error === null && coincidencias !== null && coincidencias.length > 0 && (
          <p className="eyebrow">
            {coincidencias.length === 1 ? 'Una coincidencia' : `${coincidencias.length} coincidencias`}
          </p>
        )}

        {/* Sin resultados no es un error: se explica dentro del mismo anuncio. */}
        {!buscando && error === null && coincidencias !== null && coincidencias.length === 0 && (
          <div className="border-t border-palo/70 pt-6">
            <p className="font-display text-xl text-tinta">No encontramos ese nombre</p>
            <p className="mt-3 leading-relaxed text-tinta/85">
              Prueba solo con tu nombre, o solo con tu apellido: puede que te hayamos anotado de otra
              manera. Si aun así no apareces, escríbeles a los novios y te agregan en un minuto.
            </p>
            <Link to={`/b/${boda.slug}`} className={cx(BOTON_CONTORNO, 'mt-6')}>
              Volver a la invitación
            </Link>
          </div>
        )}
      </div>

      {!buscando && error !== null && (
        <div className="mt-4">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}

      {!buscando && error === null && coincidencias !== null && coincidencias.length > 0 && (
        <ul className="mt-3 border-t border-palo/70">
          {coincidencias.map((coincidencia) => (
            <li key={coincidencia.id}>
              <button
                type="button"
                onClick={() => onElegir(coincidencia)}
                className="flex min-h-[64px] w-full items-center justify-between gap-4 border-b border-palo/70 px-1 py-4 text-left transition-colors hover:bg-palo/30"
              >
                <span className="font-display text-xl text-tinta">{coincidencia.fullName}</span>
                <ChevronRight size={18} aria-hidden="true" className="shrink-0 text-palo-hondo" />
              </button>
            </li>
          ))}
        </ul>
      )}

    </>
  )
}

// ---------------------------------------------------------------------------
// Paso 2 — responder
// ---------------------------------------------------------------------------

const OPCION =
  'flex min-h-[64px] w-full items-center justify-center rounded-md border px-5 py-4 ' +
  'text-base font-medium transition-colors'
const OPCION_APAGADA = 'border-palo bg-lienzo text-tinta hover:bg-palo/35'
const OPCION_ENCENDIDA = 'border-tinta bg-tinta text-bruma'

interface PasoResponderProps {
  tituloRef: RefObject<HTMLHeadingElement | null>
  boda: PublicWedding
  invitado: Coincidencia
  asiste: boolean | null
  onAsiste: (valor: boolean) => void
  codigo: string
  onCodigo: (valor: string) => void
  acompanantes: string
  onAcompanantes: (valor: string) => void
  dieta: string
  onDieta: (valor: string) => void
  mensaje: string
  onMensaje: (valor: string) => void
  enviando: boolean
  error: string | null
  onEnviar: (evento: FormEvent<HTMLFormElement>) => void
  onOtroNombre: () => void
}

function PasoResponder({
  tituloRef,
  boda,
  invitado,
  asiste,
  onAsiste,
  codigo,
  onCodigo,
  acompanantes,
  onAcompanantes,
  dieta,
  onDieta,
  mensaje,
  onMensaje,
  enviando,
  error,
  onEnviar,
  onOtroNombre,
}: PasoResponderProps) {
  const rsvp = boda.settings.rsvp
  const fecha = fechaCompleta(boda.weddingDate)

  return (
    <>
      <button
        type="button"
        onClick={onOtroNombre}
        className="inline-flex min-h-[44px] items-center gap-2 text-sm text-palo-hondo transition-colors hover:text-tinta"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        No soy yo, buscar otra vez
      </button>

      <p className="eyebrow mt-6">Tu respuesta</p>
      <h2 ref={tituloRef} tabIndex={-1} className="mt-3 font-display text-titulo text-tinta">
        {invitado.fullName.trim() !== '' ? invitado.fullName : 'Tu invitación'}
      </h2>
      <p className="mt-4 leading-relaxed text-tinta/85">
        {fecha === '' ? '¿Nos acompañas?' : `¿Nos acompañas el ${fecha}?`}
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          aria-pressed={asiste === true}
          onClick={() => onAsiste(true)}
          className={cx(OPCION, asiste === true ? OPCION_ENCENDIDA : OPCION_APAGADA)}
        >
          Sí, allí estaré
        </button>
        <button
          type="button"
          aria-pressed={asiste === false}
          onClick={() => onAsiste(false)}
          className={cx(OPCION, asiste === false ? OPCION_ENCENDIDA : OPCION_APAGADA)}
        >
          No podré ir
        </button>
      </div>

      {asiste !== null && (
        <form className="mt-8 flex flex-col gap-5" onSubmit={onEnviar}>
          {(rsvp.requireCode || asiste) && (
            <div className="flex flex-col gap-5 sm:grid sm:grid-cols-2 sm:gap-4">
              {rsvp.requireCode && (
                <Field
                  label="Código de tu invitación"
                  value={codigo}
                  onChange={(evento) => onCodigo(evento.target.value.toUpperCase())}
                  hint="Son las letras y números que vienen en la invitación que recibiste."
                  autoComplete="off"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={40}
                  className="min-h-[52px] font-mono text-base uppercase tracking-widest"
                />
              )}

              {asiste && (
                /* Arranca vacío, no en «0»: en un teléfono ese cero hay que
                   seleccionarlo y borrarlo antes de escribir el número real.
                   `acompanantesValidos` ya trata el vacío como cero, así que
                   quien viene solo puede seguir sin tocar el campo. */
                <Field
                  label="¿Cuántas personas te acompañan?"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={MAX_ACOMPANANTES}
                  step={1}
                  placeholder="0"
                  value={acompanantes}
                  onChange={(evento) => onAcompanantes(evento.target.value)}
                  hint="Sin contarte a ti. Si tu invitación tiene menos cupos, te lo decimos en la siguiente pantalla."
                  className="tabular min-h-[52px] font-mono text-base"
                />
              )}
            </div>
          )}

          {asiste && (
            <TextArea
              label="¿Alguna restricción de comida? (opcional)"
              value={dieta}
              onChange={(evento) => onDieta(evento.target.value)}
              hint="Alergias, vegetariano, sin gluten… lo que la cocina deba saber."
              maxLength={500}
              rows={3}
              className="text-base"
            />
          )}

          {rsvp.allowMessages && (
            <TextArea
              label="Déjales un mensaje (opcional)"
              value={mensaje}
              onChange={(evento) => onMensaje(evento.target.value)}
              hint={
                rsvp.moderateMessages
                  ? 'Aparecerá en el libro de mensajes cuando los novios lo aprueben.'
                  : 'Se publica en el libro de mensajes de la invitación.'
              }
              maxLength={1000}
              rows={4}
              className="text-base"
            />
          )}

          {error !== null && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" loading={enviando} full className="min-h-[52px] text-base">
            {asiste ? 'Confirmar mi asistencia' : 'Enviar mi respuesta'}
          </Button>
        </form>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Paso 3 — hecho
// ---------------------------------------------------------------------------

interface PasoHechoProps {
  tituloRef: RefObject<HTMLHeadingElement | null>
  boda: PublicWedding
  respuesta: Respuesta
  onCambiar: () => void
}

function PasoHecho({ tituloRef, boda, respuesta, onCambiar }: PasoHechoProps) {
  const { guest, pedidos, dejoMensaje } = respuesta
  const asistio = guest.status === 'confirmado'
  const rsvp = boda.settings.rsvp

  return (
    <>
      <CordMark className="h-4 w-10 text-laton" />

      <p className="eyebrow mt-6">Listo</p>
      <h2 ref={tituloRef} tabIndex={-1} className="mt-3 font-display text-titulo text-tinta">
        {asistio ? 'Confirmado' : 'Gracias por avisarnos'}
      </h2>

      {asistio ? (
        <>
          <p className="mt-4 text-lg leading-relaxed text-tinta/85">
            {`Nos vemos, ${primerNombre(guest.fullName)}. Quedaste en la lista.`}
          </p>
          <Acompanantes confirmados={guest.confirmedCompanions} pedidos={pedidos} />
          <Detalles boda={boda} />
        </>
      ) : (
        <>
          <p className="mt-4 text-lg leading-relaxed text-tinta/85">
            {`Te vamos a extrañar, ${primerNombre(guest.fullName)}. Gracias por decírnoslo con tiempo: así organizamos las mesas sabiendo quién viene.`}
          </p>
          <p className="mt-4 leading-relaxed text-tinta/85">
            Si tus planes cambian, vuelve a esta página y respóndenos de nuevo. No hay problema.
          </p>
        </>
      )}

      {dejoMensaje && (
        <p className="mt-6 rounded-md bg-lienzo px-4 py-3 text-sm leading-relaxed text-palo-hondo">
          {rsvp.moderateMessages
            ? 'Tu mensaje llegó. Aparecerá en el libro de la invitación cuando los novios lo aprueben.'
            : 'Tu mensaje ya está en el libro de la invitación.'}
        </p>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link to={`/b/${boda.slug}`} className={BOTON_CLARO}>
          Volver a la invitación
        </Link>
        {asistio && (
          <Link to={`/b/${boda.slug}/pase/${guest.id}`} className={BOTON_CONTORNO}>
            Ver mi pase de invitado
          </Link>
        )}
        {asistio && boda.weddingDate !== null && (
          <button
            type="button"
            onClick={() => agregarAlCalendario(boda)}
            className={BOTON_CONTORNO}
          >
            Agregar a mi calendario
          </button>
        )}
        {rsvp.allowGuestUploads && (
          <Link to={`/b/${boda.slug}/subir`} className={BOTON_CONTORNO}>
            Subir fotos
          </Link>
        )}
      </div>

      <hr className="regla my-8" />

      <p className="text-sm text-palo-hondo">
        ¿Te equivocaste o cambiaron tus planes?{' '}
        <button
          type="button"
          onClick={onCambiar}
          className="font-medium text-laton underline underline-offset-4 transition-colors hover:text-tinta"
        >
          Cambiar mi respuesta
        </button>
      </p>
    </>
  )
}

/** El número real lo decide el servidor con el cupo de la invitación. */
function Acompanantes({ confirmados, pedidos }: { confirmados: number; pedidos: number }) {
  if (confirmados === 0 && pedidos === 0) {
    return <p className="mt-4 leading-relaxed text-tinta/85">Te esperamos a ti.</p>
  }

  if (confirmados === 0) {
    return (
      <p className="mt-4 leading-relaxed text-tinta/85">
        Anotamos tu lugar. Tu invitación no tiene cupo para acompañantes: si necesitas uno,
        escríbeles a los novios.
      </p>
    )
  }

  return (
    <>
      <p className="mt-4 leading-relaxed text-tinta/85">
        {`Te esperamos con ${plural(confirmados, 'acompañante', 'acompañantes')}.`}
      </p>
      {pedidos > confirmados && (
        <p className="mt-2 text-sm leading-relaxed text-palo-hondo">
          {`Pediste ${plural(pedidos, 'acompañante', 'acompañantes')} y tu invitación alcanza para ${confirmados}. Si necesitas más lugares, escríbeles a los novios.`}
        </p>
      )}
    </>
  )
}

/** Descarga el evento para el calendario del teléfono: ahí viven los recordatorios. */
function agregarAlCalendario(boda: PublicWedding): void {
  const evento = boda.settings.event
  const lugar = [evento.ceremonyVenue, evento.ceremonyAddress]
    .map((parte) => parte.trim())
    .filter((parte) => parte !== '')
    .join(', ')

  descargarIcs(
    `boda-${boda.slug}.ics`,
    icsDeBoda({
      titulo: `Boda de ${boda.partner1Name} y ${boda.partner2Name}`,
      descripcion: 'La invitación, con el mapa y todos los detalles:',
      lugar,
      fecha: boda.weddingDate ?? '',
      hora: evento.ceremonyTime,
      url: `${window.location.origin}/b/${boda.slug}`,
    }),
  )
}

/** Fecha, hora y lugar repetidos aquí: es la pantalla que el invitado guarda. */
function Detalles({ boda }: { boda: PublicWedding }) {
  const evento = boda.settings.event
  const ceremonia =
    tieneTexto(evento.ceremonyVenue) ||
    tieneTexto(evento.ceremonyAddress) ||
    tieneTexto(evento.ceremonyTime)

  const lugar = ceremonia
    ? {
        etiqueta: 'Ceremonia',
        venue: evento.ceremonyVenue,
        address: evento.ceremonyAddress,
        time: evento.ceremonyTime,
        maps: evento.ceremonyMapsUrl,
      }
    : {
        etiqueta: 'Recepción',
        venue: evento.receptionVenue,
        address: evento.receptionAddress,
        time: evento.receptionTime,
        maps: evento.receptionMapsUrl,
      }

  const fecha = fechaCompleta(boda.weddingDate)
  const mapa = urlSegura(lugar.maps)
  const hayLugar = tieneTexto(lugar.venue) || tieneTexto(lugar.address) || tieneTexto(lugar.time)

  if (fecha === '' && !hayLugar) return null

  return (
    <div className="mt-8 border-t border-palo/70 pt-6">
      {fecha !== '' && <p className="font-display text-xl text-tinta">{fecha}</p>}

      {hayLugar && (
        <div className="mt-4">
          <p className="eyebrow">{lugar.etiqueta}</p>
          {tieneTexto(lugar.time) && (
            <p className="tabular mt-2 font-mono text-sm text-tinta">{lugar.time}</p>
          )}
          {tieneTexto(lugar.venue) && <p className="mt-2 text-tinta">{lugar.venue}</p>}
          {tieneTexto(lugar.address) && (
            <p className="mt-1 leading-relaxed text-tinta/85">{lugar.address}</p>
          )}
          {mapa && (
            <EnlaceExterno
              href={mapa}
              className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-laton underline underline-offset-4 hover:text-tinta"
            >
              <MapPin size={16} aria-hidden="true" />
              Ver en el mapa
            </EnlaceExterno>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estados de página completa
// ---------------------------------------------------------------------------

function Cargando() {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-tinta px-6">
      <div className="flex flex-col items-start gap-4 text-palo">
        <CordMark className="h-4 w-10 text-laton-claro" />
        <p className="flex items-center gap-3 text-sm">
          <Spinner small />
          Abriendo la confirmación…
        </p>
      </div>
    </main>
  )
}

function Envoltura({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center bg-bruma px-6 py-16 sm:px-12">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </main>
  )
}

function SinBoda() {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No encontramos esa boda</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">
        El enlace puede tener una letra de más, o los novios todavía no publicaron su página.
        Revísalo con quien te invitó: es una dirección del tipo{' '}
        <span className="font-mono text-tinta">/b/ana-y-carlos/confirmar</span>.
      </p>
      <Link to="/" className={cx(BOTON_CONTORNO, 'mt-8')}>
        Conocer Nuestra Boda
      </Link>
    </Envoltura>
  )
}

function FalloDeCarga({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No pudimos abrir la confirmación</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">{mensaje}</p>
      <button type="button" onClick={onReintentar} className={cx(BOTON_CLARO, 'mt-8')}>
        Intentar de nuevo
      </button>
    </Envoltura>
  )
}
